import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, Building2, Layers, ShieldCheck, LogOut, Settings as SettingsIcon, UserCog, Landmark, FileStack } from "lucide-react";
import NotificationsBell from "@/components/NotificationsBell";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", staffOnly: true },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/business", label: "Business Setup", icon: Building2, testid: "nav-business" },
  { to: "/tradelines", label: "Tradelines", icon: Layers, testid: "nav-tradelines" },
  { to: "/private-banking", label: "Private Banking", icon: Landmark, testid: "nav-private-banking", staffOnly: true },
  { to: "/templates", label: "Letter Templates", icon: FileStack, testid: "nav-templates", staffOnly: true },
  { to: "/team", label: "Team & Codes", icon: UserCog, testid: "nav-team", staffOnly: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", staffOnly: true },
];

const ROLE_LABEL = { master: "Master", admin: "Admin Manager", agent: "Agent" };

export default function Layout({ children }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const isAgent = user?.role === "agent";
  const nav = NAV.filter((n) => !(n.staffOnly && isAgent));

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <div className="min-h-screen flex relative z-10">
      <aside className="w-64 shrink-0 border-r border-border bg-card/60 backdrop-blur-xl flex-col hidden md:flex fixed h-screen">
        <div className="px-6 py-6 border-b border-border flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-base leading-none tracking-tight">Urbanvision</div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground mt-1">Financial Solutions</div>
            </div>
          </Link>
          {!isAgent && <NotificationsBell />}
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map((n) => {
            const Icon = n.icon;
            const active = isActive(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon className="h-[18px] w-[18px]" />
                {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 mb-1">
            <div className="text-sm font-medium truncate">{user?.name || "Admin"}</div>
            <div className="text-xs text-primary truncate">{ROLE_LABEL[user?.role] || ""}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-muted-foreground hover:bg-destructive/15 hover:text-destructive transition-colors"
          >
            <LogOut className="h-[18px] w-[18px]" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 md:ml-64 min-w-0">
        <div className="md:hidden flex items-center justify-between border-b border-border px-4 py-3 bg-card/60 backdrop-blur-xl sticky top-0 z-20">
          <span className="font-display font-bold">Urbanvision Financial</span>
          <div className="flex items-center gap-1">
            {!isAgent && <NotificationsBell testId="notifications-bell-mobile" />}
            <button onClick={logout} data-testid="logout-btn-mobile" className="text-muted-foreground">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="md:hidden flex gap-1 overflow-x-auto px-3 py-2 border-b border-border bg-card/40">
          {nav.map((n) => (
            <Link key={n.to} to={n.to} className={`px-3 py-1.5 rounded-md text-xs whitespace-nowrap ${isActive(n.to) ? "bg-primary/15 text-primary" : "text-muted-foreground"}`}>
              {n.label}
            </Link>
          ))}
        </div>
        <div className="p-5 sm:p-8 max-w-7xl mx-auto">{children}</div>
      </main>
    </div>
  );
}
